import { getFailed, getUnsynced, markFailed, removeFromQueue, type QueuedContribution } from './offlineQueue';
import { ApiError, apiFetch, uploadMedia, uploadPhoto } from '../api/client';
import { trackCaptureEvent } from './captureAnalytics';

// Event the PWA can dispatch to ContributePage to surface failed queue items.
// Detail carries the current count of items with a persisted lastError.
export const QUEUE_FAILURE_EVENT = 'st:queue-failure';

function dispatchQueueFailureCount(count: number): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(QUEUE_FAILURE_EVENT, { detail: { count } }));
}

function isPersistentValidation(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  // 4xx with a stable message — retrying won't help until user takes action.
  if (err.code === 'TEMPLATE_VALIDATION') return true;
  if (err.code === 'VALIDATION_ERROR') return true;
  return false;
}

export async function getFailedQueueCount(): Promise<number> {
  try {
    const failed = await getFailed();
    return failed.length;
  } catch {
    return 0;
  }
}

function hasCurrentAuthSession(): boolean {
  if (localStorage.getItem('access_token')) {
    return true;
  }

  const rawGuestSession = localStorage.getItem('guest_session');
  if (!rawGuestSession) {
    return false;
  }

  try {
    const session = JSON.parse(rawGuestSession) as { expiresAt?: string };
    if (!session.expiresAt || new Date(session.expiresAt) <= new Date()) {
      localStorage.removeItem('guest_session');
      return false;
    }
    return true;
  } catch {
    localStorage.removeItem('guest_session');
    return false;
  }
}

export async function syncPendingContributions(): Promise<number> {
  // Guard: skip sync if no auth state exists, to avoid the 401 path that wipes
  // tokens and redirects. The server-issued guest_token cookie is httpOnly and
  // unreadable from JS, so current local auth state is the closest client-side
  // proxy; resumable guest_sessions are not proof of an active auth cookie.
  const hasSession = hasCurrentAuthSession();
  if (!hasSession) {
    console.warn('[background-sync] No auth session, skipping sync');
    return 0;
  }

  const pending = await getUnsynced();
  let synced = 0;

  for (const contribution of pending) {
    try {
      if (contribution.contributionType === 'text') {
        const body: Record<string, unknown> = {
          contributionType: 'text',
          content: contribution.content,
          memoryDate: contribution.memoryDate,
          visibility: contribution.visibility ?? 'public',
        };
        if (contribution.buildsOnContributionId) {
          body.buildsOnContributionId = contribution.buildsOnContributionId;
        }
        if (contribution.metadata) {
          body.metadata = contribution.metadata;
        }
        if (contribution.arcShape) {
          body.arcShape = contribution.arcShape;
        }
        await apiFetch(`/threads/${contribution.threadId}/contributions`, {
          method: 'POST',
          headers: { 'X-Idempotency-Key': contribution.id },
          body: JSON.stringify(body),
        });
      } else if (
        contribution.contributionType === 'photo' ||
        contribution.contributionType === 'audio' ||
        contribution.contributionType === 'video'
      ) {
        if (!contribution.mediaBlob) {
          console.warn(
            `[background-sync] ${contribution.contributionType} contribution ${contribution.id} has no blob, skipping.`
          );
          continue;
        }

        const defaults: Record<string, { filename: string; contentType: string }> = {
          photo: { filename: `photo-${contribution.id}.jpg`, contentType: 'image/jpeg' },
          audio: { filename: `audio-${contribution.id}.webm`, contentType: 'audio/webm' },
          video: { filename: `video-${contribution.id}.webm`, contentType: 'video/webm' },
        };
        const fallback = defaults[contribution.contributionType];

        // Upload media to S3 via presigned URL flow
        const mediaUrl = await uploadMedia(
          contribution.threadId,
          contribution.mediaBlob,
          contribution.mediaFilename ?? fallback.filename,
          contribution.mediaContentType ?? fallback.contentType,
        );

        let mediaThumbnailUrl: string | undefined;
        if (contribution.contributionType === 'video' && contribution.mediaThumbnailBlob) {
          try {
            mediaThumbnailUrl = await uploadPhoto(
              contribution.threadId,
              contribution.mediaThumbnailBlob,
              contribution.mediaThumbnailFilename ?? `video-thumb-${contribution.id}.jpg`,
              contribution.mediaThumbnailContentType ?? 'image/jpeg',
            );
          } catch (thumbnailError) {
            console.warn(`[background-sync] Thumbnail upload failed for ${contribution.id}, continuing without thumbnail`, thumbnailError);
            mediaThumbnailUrl = undefined;
          }
        }

        // Create contribution with the S3 media URL
        const body: Record<string, unknown> = {
          contributionType: contribution.contributionType,
          content: contribution.content,
          mediaUrl,
          memoryDate: contribution.memoryDate,
          visibility: contribution.visibility ?? 'public',
        };
        body.mediaType = contribution.mediaContentType ?? fallback.contentType;
        if (contribution.mediaDuration != null) {
          body.mediaDuration = contribution.mediaDuration;
        }
        if (mediaThumbnailUrl) {
          body.mediaThumbnailUrl = mediaThumbnailUrl;
        }
        if (contribution.mediaWidth != null) {
          body.mediaWidth = contribution.mediaWidth;
        }
        if (contribution.mediaHeight != null) {
          body.mediaHeight = contribution.mediaHeight;
        }
        if (contribution.buildsOnContributionId) {
          body.buildsOnContributionId = contribution.buildsOnContributionId;
        }
        if (contribution.metadata) {
          body.metadata = contribution.metadata;
        }
        if (contribution.arcShape) {
          body.arcShape = contribution.arcShape;
        }
        await apiFetch(`/threads/${contribution.threadId}/contributions`, {
          method: 'POST',
          headers: { 'X-Idempotency-Key': contribution.id },
          body: JSON.stringify(body),
        });
      }

      // Remove synced contribution from IndexedDB queue
      await removeFromQueue(contribution.id);
      synced++;
    } catch (err) {
      // Persistent backend rejection (e.g. role / arc gate). Persist on the
      // queue item so ContributePage can surface a recovery banner instead of
      // silently retrying forever.
      if (isPersistentValidation(err)) {
        const apiErr = err as ApiError;
        await markFailed(contribution.id, {
          code: apiErr.code,
          message: apiErr.message,
          at: new Date().toISOString(),
        }).catch(() => {});
        console.warn(`[background-sync] Persistent failure on ${contribution.id}: ${apiErr.message}`);
        // A contribution the user believes they made will never be delivered.
        trackCaptureEvent('queue_item_abandoned', {
          surface: 'offline-queue',
          reason: 'persistent_validation',
          code: apiErr.code,
          threadId: contribution.threadId,
        });
      } else {
        // Transient (network, 5xx, etc.) — leave in queue for next online event.
        console.warn(`[background-sync] Transient failure on ${contribution.id}`, err);
        trackCaptureEvent('queue_sync_failed', {
          surface: 'offline-queue',
          reason: 'transient',
          threadId: contribution.threadId,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }

  const failedCount = await getFailedQueueCount();
  if (failedCount > 0) {
    dispatchQueueFailureCount(failedCount);
  }

  return synced;
}

// Listen for online events to trigger sync
export function setupBackgroundSync(): void {
  window.addEventListener('online', () => {
    syncPendingContributions().catch(console.error);
  });

  // Listen for sync messages from service worker
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'sync-contributions') {
        syncPendingContributions().catch(console.error);
      }
    });
  }

  // Also register for Background Sync API if available
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    navigator.serviceWorker.ready.then((registration) => {
      (registration as any).sync?.register('sync-contributions').catch(console.error);
    });
  }
}
